import type { Article, ArticleOverviewContext, Blog, BlogData } from "./models";
import {
  articleExcerpt,
  articlePlainText,
  authorPath,
  blogPath,
  blogTopics,
  defaultNewStyleBlog,
  newStyleBlogs,
  overviewPagePath,
  sortNewestFirst,
  topicPath,
} from "./article-paths";

/** The first of each is the standard. */
export const ARTICLE_FILTERS = [
  "all",
  "page",
  "topic",
  "author",
  "featured",
] as const;
export const ARTICLE_PAGINATIONS = ["none", "pages", "more"] as const;

/** The Artikler section's settings that choose and page its articles. */
export interface ArticleListSettings {
  /** 0 Redaktionelt udvalg, 1 Seneste artikler, 2 En fremhævet artikel */
  variant: number;
  sourceMode?: "auto" | "selected";
  selectedIds?: string[];
  blogId?: string;
  articleFilter?: (typeof ARTICLE_FILTERS)[number];
  topicId?: string;
  authorId?: string;
  pagination?: (typeof ARTICLE_PAGINATIONS)[number];
  topicTabs?: boolean;
  showSearch?: boolean;
  /** Articles per list or page; 3 when absent. */
  count?: number;
}

export interface ArticleListLink {
  label: string;
  href: string;
  current: boolean;
}

export interface ArticleListPlan {
  blog: Blog;
  posts: Article[];
  /** The search the list answers, when it is on the overview it searches. */
  search?: string;
  /** Page links (`pagination: "pages"`) when there is more than one page. */
  pages?: {
    current: number;
    links: ArticleListLink[];
    previous?: string;
    next?: string;
  };
  /** »Vis flere« to the next page of the overview the list is on (`pagination: "more"`). */
  more?: string;
  /** »Se alle …« to the overview, from a list on another page that has more articles than it shows. */
  all?: string;
  /** How many pages the list has when it answers the address's page number. */
  lastPage?: number;
  tabs?: ArticleListLink[];
  /** The address the search box sends `?soeg=` to. */
  searchAction?: string;
}

export interface ArticleListContext {
  current?: Article;
  overview?: ArticleOverviewContext;
  /** Articles an »En fremhævet artikel« higher on the same page already shows. */
  shown?: string[];
  /** Tabs, search and links lead to the overview, so without one there are none; assumed when absent. */
  hasOverview?: (blog: Blog) => boolean;
}

/**
 * A `blogId` that names neither an artikelside nor an article of `data`, as after the artikelside was deleted: any list
 * drawn for it would be empty. Only on websites with artikelsider that have a path, so legacy-only websites keep their
 * empty state.
 */
export function namesMissingBlog(
  data: BlogData | undefined,
  blogId: string | undefined,
): boolean {
  return (
    !!data &&
    !!blogId &&
    newStyleBlogs(data).length > 0 &&
    !(Array.isArray(data.blogs) ? data.blogs : []).some(
      (blog) => blog?.id === blogId,
    ) &&
    !(Array.isArray(data.posts) ? data.posts : []).some(
      (post) => post?.blogId === blogId,
    )
  );
}

/**
 * The section's artikelside: the one `blogId` names when it has a path; a section naming a legacy artikelside keeps
 * the legacy list, and one naming no artikelside has none. Otherwise the artikelside whose overview this is, else the
 * default one; none without artikelsider that have a path.
 */
export function articleListBlog(
  data: BlogData | undefined,
  blogId: string | undefined,
  overview?: ArticleOverviewContext,
): Blog | undefined {
  const blogs = newStyleBlogs(data);
  if (!blogs.length) return undefined;
  const named = blogId
    ? (data?.blogs ?? []).find((blog) => blog.id === blogId)
    : undefined;
  if (named) return blogPath(named) ? named : undefined;
  if (blogId) return undefined;
  return (
    blogs.find((blog) => blog.id === overview?.blogId) ??
    defaultNewStyleBlog(data)
  );
}

const matches = (post: Article, search: string) =>
  `${post.title} ${articleExcerpt(post)} ${articlePlainText(post)}`
    .toLocaleLowerCase("da-DK")
    .includes(search.toLocaleLowerCase("da-DK"));

/** Which articles a new-style Artikler section shows, and the links around them; undefined keeps the legacy list. */
export function planArticleList(
  data: BlogData | undefined,
  settings: ArticleListSettings,
  context: ArticleListContext = {},
): ArticleListPlan | undefined {
  const blog = articleListBlog(data, settings.blogId, context.overview);
  if (!blog || !data) return undefined;
  const path = blogPath(blog)!;
  const linked = context.hasOverview?.(blog) ?? true;
  const selected = settings.sourceMode === "selected";
  const posts = (data.posts ?? []).filter(
    (post) => post.id !== context.current?.id,
  );
  const own = posts.filter((post) => post.blogId === blog.id);
  const route =
    context.overview?.blogId === blog.id ? context.overview : undefined;
  const topics = blogTopics(data);
  const routeTopic = topics.find((topic) => topic.id === route?.topicId);
  const routeAuthor = (data.authors ?? []).find(
    (author) => author.id === route?.authorId,
  );
  const routeBase = routeTopic
    ? topicPath(path, routeTopic)
    : routeAuthor
      ? authorPath(path, routeAuthor)
      : path;
  const filter = settings.articleFilter ?? "all";
  const featuredOnly = settings.variant === 2;
  const follows = !selected && filter === "page";
  const base = follows ? routeBase : path;
  /** The list answers this address's page number and search: it is on its own overview. */
  const onRoute = !!route && base === routeBase;
  const search =
    onRoute && !selected && !featuredOnly && route.search?.trim()
      ? route.search.trim().slice(0, 100)
      : undefined;
  if (featuredOnly && route && (route.pageNumber > 1 || route.search?.trim()))
    return { blog, posts: [] };
  /** The featured article of a topic or author page is one of its own. */
  const followsRoute =
    follows ||
    (featuredOnly && !selected && ["all", "featured"].includes(filter));
  /** »Vis flere« shows every article up to its page, and on page 2 or later the featured article above is not drawn. */
  const shown =
    selected ||
    (onRoute && settings.pagination === "more" && route.pageNumber > 1)
      ? []
      : (context.shown ?? []);
  let list: Article[];
  if (selected) {
    list = (settings.selectedIds ?? []).flatMap((id) => {
      const post = posts.find((item) => item.id === id);
      return post ? [post] : [];
    });
  } else {
    const topicId =
      filter === "topic"
        ? settings.topicId
        : followsRoute
          ? route?.topicId
          : undefined;
    const authorId =
      filter === "author"
        ? settings.authorId
        : followsRoute
          ? route?.authorId
          : undefined;
    list = sortNewestFirst(
      own.filter(
        (post) =>
          !shown.includes(post.id) &&
          (!topicId || (post.topicIds ?? []).includes(topicId)) &&
          (!authorId || (post.authorIds ?? []).includes(authorId)) &&
          (filter !== "featured" || post.featured === true) &&
          (!search || matches(post, search)),
      ),
    );
  }
  if (featuredOnly) {
    const pick = selected
      ? list[0]
      : (list.find((post) => post.featured === true) ?? list[0]);
    return { blog, posts: pick ? [pick] : [] };
  }
  const count = Math.max(
    1,
    Math.min(100, Math.round(settings.count ?? 3) || 3),
  );
  const pageCount = Math.max(1, Math.ceil(list.length / count));
  const paged =
    onRoute &&
    (settings.pagination === "pages" || settings.pagination === "more");
  const page = paged ? Math.max(1, route.pageNumber) : 1;
  const query = search ? `?soeg=${encodeURIComponent(search)}` : "";
  const href = (number: number) => overviewPagePath(base, number) + query;
  const plan: ArticleListPlan = {
    blog,
    posts:
      settings.pagination === "more"
        ? list.slice(0, page * count)
        : list.slice((page - 1) * count, page * count),
    ...(search ? { search } : {}),
    ...(paged ? { lastPage: pageCount } : {}),
  };
  const further = list.length > page * count;
  if (linked && settings.pagination === "pages" && pageCount > 1 && onRoute)
    plan.pages = {
      current: page,
      links: Array.from({ length: pageCount }, (_, i) => i + 1)
        .filter(
          (number) =>
            number === 1 || number === pageCount || Math.abs(number - page) < 3,
        )
        .map((number) => ({
          label: String(number),
          href: href(number),
          current: number === page,
        })),
      ...(page > 1 ? { previous: href(page - 1) } : {}),
      ...(page < pageCount ? { next: href(page + 1) } : {}),
    };
  else if (linked && settings.pagination === "more" && onRoute && further)
    plan.more = href(page + 1);
  else if (
    linked &&
    (settings.pagination === "more" || settings.pagination === "pages") &&
    further
  )
    plan.all = base;
  if (linked && settings.topicTabs && blog.topicPages !== false) {
    const used = topics.filter((topic) =>
      own.some((post) => (post.topicIds ?? []).includes(topic.id)),
    );
    if (used.length)
      plan.tabs = [
        {
          label: "Alle",
          href: path,
          current: !!route && !route.topicId && !route.authorId,
        },
        ...used.map((topic) => ({
          label: topic.name,
          href: topicPath(path, topic),
          current: route?.topicId === topic.id,
        })),
      ];
  }
  if (linked && settings.showSearch && !selected) plan.searchAction = base;
  return plan;
}
